import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { basePath } from '@/utils/basePath';
import { getAllSlugs, getNewsletterBySlug, Newsletter } from '@/utils/newsletters';
import { buildArticleSchema } from '@/utils/schema';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { marked } from 'marked';
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import { useMemo, useRef } from 'react';

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateStr));
}

type Props = { newsletter: Newsletter; seo: SEOProps };

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: getAllSlugs().map((slug) => ({ params: { slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const newsletter = getNewsletterBySlug(params!.slug as string);
  const seo: SEOProps = {
    title: `${newsletter.title} | zLawyer`,
    description: newsletter.description,
    url: `https://www.zlawyer.fr${basePath}/newsletters/${newsletter.slug}/`,
    type: 'article',
    datePublished: newsletter.date,
    keywords: `newsletter avocat, ${newsletter.title.toLowerCase()}, zLawyer, cabinet avocat`,
    schema: buildArticleSchema({
      title: newsletter.title,
      description: newsletter.description,
      slug: newsletter.slug,
      date: newsletter.date,
    }),
  };
  return { props: { newsletter, seo } };
};

export default function NewsletterDetail({
  newsletter,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const refs = {
    page: useRef<HTMLElement>(null),
    back: useRef(null),
    meta: useRef<HTMLDivElement>(null),
    title: useRef(null),
    hr: useRef<HTMLDivElement>(null),
    body: useRef<HTMLDivElement>(null),
  };

  const htmlBody = useMemo(
    () => (newsletter.body ? marked(newsletter.body) : ''),
    [newsletter.body],
  );

  useGSAP(
    () => {
      if (!refs.page.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });

      gsap.set([refs.back.current, refs.meta.current, refs.hr.current, refs.body.current], {
        autoAlpha: 0,
        y: 10,
      });

      const tl = gsap.timeline({
        delay: 0.35,
        defaults: { duration: MOTION.durationSm, ease: MOTION.ease },
      });

      tl.to(refs.back.current, { autoAlpha: 1, y: 0 })
        .to(refs.meta.current, { autoAlpha: 1, y: 0 }, '<+0.08')
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.1',
        )
        .to(refs.hr.current, { autoAlpha: 1, y: 0 }, '<+0.08')
        .to(refs.body.current, { autoAlpha: 1, y: 0 }, '<+0.08');

      return () => {
        splitTitle.revert();
      };
    },
    { scope: refs.page },
  );

  return (
    <section ref={refs.page} className="max-width-blue-section" id="newsletter-detail">
      <div className="mx-auto w-full max-w-3xl">
        <Link
          ref={refs.back}
          className="text-dark-blue/40 hover:text-orange font-roboto-medium mb-10 flex items-center gap-2 text-sm transition-colors duration-200"
          href="/newsletters"
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path d="M19 12H5M5 12l7 7M5 12l7-7" />
          </svg>
          Toutes les newsletters
        </Link>

        <div ref={refs.meta} className="mb-4 flex items-center gap-3">
          <time className="text-dark-blue/35 font-roboto-medium text-sm tabular-nums">
            {formatDate(newsletter.date)}
          </time>
          <span className="text-dark-blue/20 text-sm">·</span>
          <Tag>Newsletter</Tag>
        </div>

        <h1 ref={refs.title} className="text-dark-blue/90 font-roboto-bold mb-8 leading-tight">
          {newsletter.title}
        </h1>

        <div ref={refs.hr}>
          <hr className="border-dark-blue/8 mb-8 border-t" />
        </div>

        <div
          ref={refs.body}
          className="newsletter-body"
          dangerouslySetInnerHTML={{ __html: htmlBody }}
        />
      </div>
    </section>
  );
}

